import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Table, Button, Form, Pagination, Alert, Spinner, Card } from 'react-bootstrap';
import axios from 'axios';
import DiamondModal from '../components/DiamondModal';
import ExcelImportModal from '../components/ExcelImportModal';
import { Plus, Edit, Trash2, FileUp, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const shapes = ['Round', 'Oval', 'Pear', 'Marquise', 'Square (Princess)', 'Emerald', 'Radiant', 'Asscher', 'Cushion', 'Heart'];
  const sources = ['natural', 'lab grown'];
  const cutGrades = ['None', 'Good', 'Very Good', 'Excellent'];
  const clarities = ['SI2', 'SI1', 'VS2', 'VS1', 'VVS2', 'VVS1', 'IF', 'FL'];
  const colors = ['K', 'J', 'I', 'H', 'G', 'F', 'E', 'D'];

  const [diamonds, setDiamonds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    cut: '',
    source: '',
    carat: '',
    cut_grade: '',
    clarity: '',
    color: '',
    price: '',
    sku: ''
  });

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedDiamond, setSelectedDiamond] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
    window.location.reload();
  };

  const fetchDiamonds = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page,
        pageSize,
        ...Object.fromEntries(
            Object.entries(filters)
              .filter(([_, v]) => v !== '')
              .map(([k, v]) => {
                if (k === 'cut') return ['shapes', v.toUpperCase()];
                if (k === 'color') return ['colors', v];
                if (k === 'clarity') return ['clarities', v];
                return [k, v];
              })
        )
      });

      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/diamonds/getDiamonds?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.success) {
        setDiamonds(response.data.data);
        setTotal(response.data.total);
      }
    } catch (err) {
      console.error('Error fetching diamonds:', err);
      setError('Failed to load diamonds.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDiamonds();
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchDiamonds]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleAdd = () => {
    setSelectedDiamond(null);
    setIsEdit(false);
    setShowModal(true);
  };

  const handleEdit = (diamond) => {
    setSelectedDiamond(diamond);
    setIsEdit(true);
    setShowModal(true);
  };

  const handleSave = async (data) => {
    try {
      const payload = {
        ...data,
        cut: data.cut ? data.cut.toUpperCase() : data.cut
      };
      const token = localStorage.getItem('token');
      let response;

      if (isEdit) {
        response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/diamonds/updateDiamond`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/diamonds/addDiamond`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (response.data.success) {
        setShowModal(false);
        fetchDiamonds();
      }
    } catch (err) {
      console.error('Error saving diamond:', err);
      alert('Failed to save diamond. Please try again.');
    }
  };

  const handleImport = async (data) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/diamonds/bulkAdd`, { diamonds: data }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setShowImportModal(false);
        fetchDiamonds();
        alert(`Successfully imported ${data.length} diamonds!`);
      }
    } catch (err) {
      console.error('Error importing diamonds:', err);
      alert('Failed to import diamonds. Please check your data format.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this diamond?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/diamonds/deleteDiamond?diamond_id=${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchDiamonds();
      } catch (err) {
        console.error('Error deleting diamond:', err);
        alert('Failed to delete diamond.');
      }
    }
  };

  return (
    <div className="py-5 min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
      <Container>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
          <h2 className="fw-bold mb-0" style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-serif)' }}>Inventory Management</h2>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Form.Select 
                size="sm" 
                className="w-auto"
                value={pageSize}
                onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                }}
            >
                <option value="10">10 per page</option>
                <option value="25">25 per page</option>
                <option value="50">50 per page</option>
                <option value="100">100 per page</option>
            </Form.Select>
            <Button 
                variant="outline-primary"
                className="d-flex align-items-center gap-2" 
                onClick={() => setShowImportModal(true)}
            >
                <FileUp size={18} /> Import
            </Button>
            <Button 
                className="d-flex align-items-center gap-2" 
                onClick={handleAdd}
                style={{ backgroundColor: 'var(--color-accent)', borderColor: 'var(--color-accent)' }}
            >
                <Plus size={18} /> Add
            </Button>
            <Button 
                variant="danger"
                className="d-flex align-items-center gap-2" 
                onClick={handleLogout}
            >
                <LogOut size={18} /> Logout
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm mb-4 p-4">
          <Row className="g-3">
            <Col md={2}>
              <Form.Label className="small fw-bold">Shape</Form.Label>
              <Form.Select size="sm" name="cut" value={filters.cut} onChange={handleFilterChange}>
                <option value="">All Shapes</option>
                {shapes.map(s => <option key={s} value={s}>{s}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small fw-bold">Source</Form.Label>
              <Form.Select size="sm" name="source" value={filters.source} onChange={handleFilterChange}>
                <option value="">All Sources</option>
                {sources.map(s => <option key={s} value={s}>{s}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small fw-bold">Cut Grade</Form.Label>
              <Form.Select size="sm" name="cut_grade" value={filters.cut_grade} onChange={handleFilterChange}>
                <option value="">All Grades</option>
                {cutGrades.map(g => <option key={g} value={g}>{g}</option>)}
              </Form.Select>
            </Col>
            <Col md={1}>
              <Form.Label className="small fw-bold">Color</Form.Label>
              <Form.Select size="sm" name="color" value={filters.color} onChange={handleFilterChange}>
                <option value="">All</option>
                {colors.map(c => <option key={c} value={c}>{c}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small fw-bold">Clarity</Form.Label>
              <Form.Select size="sm" name="clarity" value={filters.clarity} onChange={handleFilterChange}>
                <option value="">All Clarities</option>
                {clarities.map(c => <option key={c} value={c}>{c}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small fw-bold">SKU</Form.Label>
              <Form.Control 
                size="sm" 
                type="text" 
                name="sku" 
                value={filters.sku} 
                onChange={handleFilterChange} 
                placeholder="Search SKU..." 
              />
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button size="sm" variant="outline-secondary" className="w-100" onClick={() => setFilters({ cut: '', source: '', carat: '', cut_grade: '', clarity: '', color: '', price: '', sku: '' })}>
                Reset Filters
              </Button>
            </Col>
          </Row>
        </Card>

        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <>
            <Card className="border-0 shadow-sm overflow-hidden mb-4">
                <Table responsive hover className="mb-0 align-middle">
                <thead style={{ backgroundColor: '#f1f4f8' }}>
                    <tr>
                    <th>Diamond Info</th>
                    <th>SKU</th>
                    <th>Source</th>
                    <th>Specifications</th>
                    <th>Dimensions</th>
                    <th>Certificate</th>
                    <th>Price</th>
                    <th className="text-end pe-4">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {diamonds.length > 0 ? diamonds.map((diamond) => (
                    <tr key={diamond.diamond_id}>
                        <td className="ps-4">
                        <div className="fw-bold">{diamond.carat} ct {diamond.cut}</div>
                        </td>
                        <td className="small fw-medium text-primary">{diamond.sku || '-'}</td>
                        <td>
                        <span className={`badge ${diamond.source === 'Lab grown' ? 'bg-info' : 'bg-primary'}`}>
                            {diamond.source}
                        </span>
                        </td>
                        <td>
                        <div className="small">Color: <strong>{diamond.color}</strong></div>
                        <div className="small">Clarity: <strong>{diamond.clarity}</strong></div>
                        <div className="small">Cut Grade: <strong>{diamond.cut_grade}</strong></div>
                        </td>
                        <td>
                        <div className="small">{diamond.measurements || '-'}</div>
                        </td>
                        <td>
                        <div className="small">{diamond.certificate_inst}</div>
                        <div className="small text-muted">{diamond.certificate_num}</div>
                        </td>
                        <td className="fw-bold text-dark">€ {diamond.price || '--'}</td>
                        <td className="text-end pe-4">
                        <Button variant="link" className="text-primary p-0 me-3" onClick={() => handleEdit(diamond)}>
                            <Edit size={18} />
                        </Button>
                        <Button variant="link" className="text-danger p-0" onClick={() => handleDelete(diamond.diamond_id)}>
                            <Trash2 size={18} />
                        </Button>
                        </td>
                    </tr>
                    )) : (
                    <tr>
                        <td colSpan="6" className="text-center py-5 text-muted">No diamonds found.</td>
                    </tr>
                    )}
                </tbody>
                </Table>
            </Card>
            
            {/* Centered Pagination at the bottom of the page content */}
            {total > pageSize && (
                <div className="d-flex justify-content-center mt-5 mb-4">
                    <Pagination className="shadow-sm">
                    <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
                    <Pagination.Prev onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} />
                    {[...Array(Math.ceil(total / pageSize))].map((_, i) => {
                        const pageNum = i + 1;
                        // Simple logic to show only a few pages if too many
                        if (pageNum === 1 || pageNum === Math.ceil(total / pageSize) || Math.abs(pageNum - page) <= 2) {
                            return (
                                <Pagination.Item key={pageNum} active={pageNum === page} onClick={() => setPage(pageNum)}>
                                    {pageNum}
                                </Pagination.Item>
                            );
                        }
                        if (pageNum === page - 3 || pageNum === page + 3) {
                            return <Pagination.Ellipsis key={pageNum} />;
                        }
                        return null;
                    })}
                    <Pagination.Next onClick={() => setPage(p => Math.min(Math.ceil(total / pageSize), p + 1))} disabled={page === Math.ceil(total / pageSize)} />
                    <Pagination.Last onClick={() => setPage(Math.ceil(total / pageSize))} disabled={page === Math.ceil(total / pageSize)} />
                    </Pagination>
                </div>
            )}
          </>
        )}
      </Container>

      <DiamondModal 
        show={showModal} 
        handleClose={() => setShowModal(false)} 
        diamond={selectedDiamond} 
        isEdit={isEdit} 
        onSave={handleSave} 
      />

      <ExcelImportModal
        show={showImportModal}
        handleClose={() => setShowImportModal(false)}
        onImport={handleImport}
      />
    </div>
  );
};

export default Dashboard;